import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import type { DdqSessionQuestion, UserActionType } from '@/types/api.types';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import {
  filterWorkspaceQuestions,
  needsReview,
  isApproved,
  type WorkspaceFilter,
} from '../utils/workspaceFilter';
import { ALIGNED_LAYOUT, stackRows } from '../utils/commentPositions';
import { useWorkspaceResponseAction } from '../hooks/useWorkspaceResponseAction';
import { WorkspaceFilterBar, type WorkspaceViewMode } from './WorkspaceFilterBar';
import { QnaIndexRow } from './QnaIndexRow';

/** Reviewer decision state recorded on a question (mirrors DdqSessionQuestion.userAction). */
type UserAction = NonNullable<DdqSessionQuestion['userAction']>;

/** Optimistic overlay for a single row: the recorded decision plus any edited answer text. */
interface ActionOverlay {
  userAction: UserAction;
  userResponse?: string;
}

export interface QnaIndexPanelProps {
  /** The session whose responses these questions belong to. */
  sessionId: string;
  /** The questions to index. */
  questions: DdqSessionQuestion[];
  /** Optional anchor accessor so click-to-jump can drive the editor host. */
  getAnchor?: (question: DdqSessionQuestion) => string | null;
  /**
   * Viewport-relative top px of each answered question's answer in the editor,
   * keyed by question id. When present and non-empty, aligned mode pins each
   * row to its answer's vertical position; when absent/empty, aligned mode
   * gracefully falls back to the compact list so the panel never renders blank.
   */
  rowPositions?: Map<string, number>;
  className?: string;
}

/** Map a persisted reviewer action back onto the local userAction field. */
const ACTION_TO_USER_ACTION: Record<UserActionType, UserAction> = {
  Accept: 'Accepted',
  Modify: 'Modified',
  Reject: 'Rejected',
};

/**
 * The filterable Q&A index — the right pane of the document workspace and the
 * real deliverable. Rows show source, number, snippet, confidence, and status;
 * filter pills and search narrow the list; clicking a row sets the shared
 * selection (driving click-to-jump in the editor host) and expands it to show
 * provenance and the Accept / Edit / Reject actions.
 *
 * Selection lives in `useWorkspaceStore` so a document editor can subscribe to
 * (and drive) the same cursor. Local optimistic state keeps the row status in
 * sync the moment an action fires; the mutation persists it to the backend.
 */
export function QnaIndexPanel({
  sessionId,
  questions,
  getAnchor,
  rowPositions,
  className,
}: QnaIndexPanelProps) {
  const [filter, setFilter] = useState<WorkspaceFilter>('all');
  const [search, setSearch] = useState('');
  // Default to the clean dense list. Cross-document linkage is carried by the
  // visible [Q#] markers stamped into the review-render (matching each row's
  // number); the Aligned rail stays available via the toggle once its editor
  // position extraction is tuned.
  const [viewMode, setViewMode] = useState<WorkspaceViewMode>('compact');
  // Local action overlay so the UI reflects a decision immediately, before
  // the mutation resolves. Carries both status and any edited answer text,
  // keyed by question id.
  const [actions, setActions] = useState<Record<string, ActionOverlay>>({});
  // Per-row error surfaced when a PATCH fails (after the optimistic overlay is
  // rolled back), keyed by question id. Cleared when a new action starts.
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});

  const activeQuestionId = useWorkspaceStore((s) => s.activeQuestionId);
  const setActiveQuestion = useWorkspaceStore((s) => s.setActiveQuestion);

  // The scroll container holding the rows (compact list or aligned rail). We
  // query the active row out of it by its stable `qna-row-{id}` id to scroll it
  // into view when the selection changes — typically driven from the editor
  // (editor → panel sync), but it also tidies up panel-internal jumps.
  const listContainerRef = useRef<HTMLDivElement>(null);
  // Only scroll when the active id actually CHANGES (not on every render), so an
  // in-progress user scroll isn't yanked back while the same row stays active.
  const lastScrolledIdRef = useRef<string | null>(null);

  const mutation = useWorkspaceResponseAction(sessionId);

  // Merge any optimistic action overlay onto the incoming questions. The row
  // prefers the overlaid answer text so an edit shows immediately on Save.
  const merged = useMemo<DdqSessionQuestion[]>(
    () =>
      questions.map((q) => {
        const overlay = actions[q.question_id];
        if (!overlay) return q;
        return {
          ...q,
          userAction: overlay.userAction,
          ...(overlay.userResponse !== undefined ? { userResponse: overlay.userResponse } : {}),
        };
      }),
    [questions, actions],
  );

  const counts = useMemo<Record<WorkspaceFilter, number>>(
    () => ({
      all: merged.length,
      'needs-review': merged.filter(needsReview).length,
      approved: merged.filter(isApproved).length,
    }),
    [merged],
  );

  const visible = useMemo(
    () => filterWorkspaceQuestions(merged, filter, search),
    [merged, filter, search],
  );

  // Do we have any positions to align to? Empty/absent → aligned mode falls
  // back to the compact list (the feature must never break the panel).
  const hasPositions = (rowPositions?.size ?? 0) > 0;

  // Aligned layout: split the visible rows into those WITH a measured position
  // (placed in the rail, collision-stacked) and those WITHOUT (unanswered /
  // NoMatch — surfaced via a compact-switch affordance, not inline).
  const aligned = useMemo(() => {
    if (!rowPositions || rowPositions.size === 0) {
      return { positioned: [], unplacedCount: 0 };
    }
    const withPos: Array<{ item: DdqSessionQuestion; top: number }> = [];
    let unplacedCount = 0;
    for (const q of visible) {
      const top = rowPositions.get(q.question_id);
      if (top == null) {
        unplacedCount += 1;
        continue;
      }
      // Apply the tunable vertical nudge so a row aligns to (or just above) its
      // answer's text. ROW_TOP_OFFSET is 0 by default — adjust in the browser.
      withPos.push({ item: q, top: top + ALIGNED_LAYOUT.ROW_TOP_OFFSET });
    }
    // Collision-stack: a row that would overlap the previous one is pushed down.
    // Heights are estimated; the active (expanded) row is taller so it doesn't
    // overlap its neighbor when opened. Real heights are hard to know before
    // layout, so these estimates are deliberately generous + easy to tune.
    const positioned = stackRows(
      withPos,
      (q) =>
        q.question_id === activeQuestionId
          ? ALIGNED_LAYOUT.ESTIMATED_ROW_HEIGHT * 4
          : ALIGNED_LAYOUT.ESTIMATED_ROW_HEIGHT,
    );
    return { positioned, unplacedCount };
  }, [rowPositions, visible, activeQuestionId]);

  // The pixel height the aligned rail needs so the last stacked row fits and the
  // rail scrolls with the document content beneath it.
  const railHeight = useMemo(() => {
    if (aligned.positioned.length === 0) return 0;
    const last = aligned.positioned[aligned.positioned.length - 1];
    return last.top + ALIGNED_LAYOUT.ESTIMATED_ROW_HEIGHT * 4 + ALIGNED_LAYOUT.ROW_GAP;
  }, [aligned.positioned]);

  // Scroll the active row into view when the selection changes (e.g. the editor
  // selected it). The row already self-expands when `isActive`, so this just
  // brings it on-screen. We guard on a ref so we don't fight an in-progress user
  // scroll while the same row stays selected — we only scroll on an actual id
  // change. `block: 'nearest'` avoids over-scrolling rows that are already
  // visible; this works for both the compact list and the aligned rail.
  useEffect(() => {
    if (activeQuestionId == null) {
      lastScrolledIdRef.current = null;
      return;
    }
    if (lastScrolledIdRef.current === activeQuestionId) return;
    lastScrolledIdRef.current = activeQuestionId;

    const container = listContainerRef.current;
    if (!container) return;
    // Query by the row's stable id rather than holding per-row refs — keeps the
    // row component untouched and works identically in both layouts. The row may
    // be absent (filtered out, or unplaced in aligned mode) → no-op.
    const rowEl = container.querySelector<HTMLElement>(
      `#qna-row-${CSS.escape(activeQuestionId)}`,
    );
    if (!rowEl) return;
    try {
      rowEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch {
      // jsdom / older engines may lack smooth-scroll support — non-fatal.
    }
  }, [activeQuestionId]);

  const handleSelect = (questionId: string) => {
    const q = merged.find((item) => item.question_id === questionId) ?? null;
    const anchor = q && getAnchor ? getAnchor(q) : null;
    setActiveQuestion(questionId, anchor);
  };

  const handleAction = (
    questionId: string,
    action: UserActionType,
    modifiedAnswer?: string,
  ) => {
    // Snapshot the prior overlay so a failed PATCH can revert exactly — the row
    // must not stay falsely marked when the backend rejects the action.
    let prevOverlay: ActionOverlay | undefined;
    setActions((prev) => {
      prevOverlay = prev[questionId];
      return {
        ...prev,
        [questionId]: {
          userAction: ACTION_TO_USER_ACTION[action],
          ...(modifiedAnswer !== undefined ? { userResponse: modifiedAnswer } : {}),
        },
      };
    });
    // Clear any stale error for this row when a new action begins.
    setActionErrors((prev) => {
      if (!(questionId in prev)) return prev;
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
    mutation.mutate(
      {
        responseId: questionId,
        request: { action, modified_answer: modifiedAnswer ?? null },
      },
      {
        onError: (err) => {
          // Roll the optimistic overlay back to its prior value and surface an
          // inline error on the row so the reviewer knows it did not persist.
          setActions((prev) => {
            const next = { ...prev };
            if (prevOverlay === undefined) {
              delete next[questionId];
            } else {
              next[questionId] = prevOverlay;
            }
            return next;
          });
          setActionErrors((prev) => ({
            ...prev,
            [questionId]:
              err instanceof Error ? err.message : 'Could not save this decision. Try again.',
          }));
        },
        onSuccess: () => {
          setActionErrors((prev) => {
            if (!(questionId in prev)) return prev;
            const next = { ...prev };
            delete next[questionId];
            return next;
          });
        },
      },
    );
  };

  // A single row, used by both layouts so select/expand/actions/provenance stay
  // identical. `style` positions it absolutely in aligned mode (omitted in
  // compact mode).
  const renderRow = (q: DdqSessionQuestion, style?: React.CSSProperties) => (
    <QnaIndexRow
      key={q.question_id}
      question={q}
      isActive={q.question_id === activeQuestionId}
      onSelect={handleSelect}
      onAction={handleAction}
      error={actionErrors[q.question_id] ?? null}
      style={style}
    />
  );

  // The dense, evenly-spaced, independently-scrolling list (today's behavior).
  const compactList = (
    <ul
      aria-label="Questions"
      className="flex-1 divide-y divide-border overflow-y-auto rounded-lg border border-border"
    >
      {visible.map((q) => renderRow(q))}
    </ul>
  );

  // Aligned mode falls back to the compact list whenever there are no positions
  // to align to — so the feature never renders a blank panel.
  const useAligned = viewMode === 'aligned' && hasPositions;

  // The aligned rail: a relative container as tall as the document's mapped
  // content, scrolling WITH the document; each positioned row is absolutely
  // placed at its (collision-stacked) top. Rows without a position (unanswered
  // / NoMatch — no comment) are NOT shown inline; a footer affordance points to
  // Compact mode instead.
  const alignedRail = (
    <div className="flex-1 overflow-y-auto rounded-lg border border-border">
      <ul aria-label="Questions" className="relative" style={{ height: railHeight || '100%' }}>
        {aligned.positioned.map(({ item, top }) =>
          renderRow(item, {
            position: 'absolute',
            top,
            left: 0,
            right: 0,
          }),
        )}
      </ul>
      {aligned.unplacedCount > 0 ? (
        <button
          type="button"
          onClick={() => setViewMode('compact')}
          className="sticky bottom-0 flex w-full items-center justify-center gap-1 border-t border-border bg-background/95 px-3 py-2 text-xs font-medium text-muted-foreground backdrop-blur transition-colors hover:bg-muted/50"
        >
          +{aligned.unplacedCount} need answer — switch to Compact
        </button>
      ) : null}
    </div>
  );

  return (
    <div ref={listContainerRef} className={cn('flex h-full flex-col gap-3', className)}>
      <WorkspaceFilterBar
        filter={filter}
        search={search}
        onFilterChange={setFilter}
        onSearchChange={setSearch}
        counts={counts}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {visible.length === 0 ? (
        <p className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">
          No questions match this filter.
        </p>
      ) : useAligned ? (
        alignedRail
      ) : (
        compactList
      )}
    </div>
  );
}
